
import { XIcon, ExclamationIcon } from "@heroicons/react/solid";
import { useState } from 'react';

/**
 * AlertError Component - Error/failure message alert
 *
 * Displays a red error alert with optional title and dismiss button.
 * Used to communicate failures, errors, validation issues, or negative outcomes.
 *
 * Features:
 * - Red themed styling (red-50 background, red-800 text)
 * - Exclamation icon from Heroicons
 * - Optional title and message
 * - Dismissible with X button (if enabled)
 * - Conditional rendering (hidden when dismissed)
 * - Shadow and rounded corners for visibility
 * - Max-width prose constraint
 * - Vertical margin (my-2) for spacing
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.title=""] - Optional bold title text
 * @param {string} [props.children=""] - Alert message content
 * @param {boolean} [props.dismissBtn=false] - Show dismiss button
 * @returns {JSX.Element|null} Error alert or null if dismissed
 *
 * @remarks
 * - Maintains local hidden state for dismissal
 * - Returns empty fragment if hidden (not rendered)
 * - Title is optional; if empty, only message displays
 * - Dismiss button has focus ring styling
 * - Red color palette used consistently
 * - Includes vertical margin for spacing from other content
 *
 * @example
 * <AlertError
 *   title="Error"
 *   dismissBtn={true}
 * >
 *   An error occurred while saving. Please try again.
 * </AlertError>
 */
export default function AlertError({ title = "", children = "", dismissBtn = false }) {
  const [hidden, setHidden] = useState(false);
  return (
    <>
      {!hidden && (
        <div role="alert" className="bg-red-50 my-2 rounded-xl shadow-md p-4 max-w-prose">
          <div className="flex items-center">
            <div className=" flex-shrink-0">
              <ExclamationIcon
                className="h-5 w-5  text-red-400"
                aria-hidden="true"
              />
            </div>
            <div className="ml-3">
              {title && (
                <h3 className="text-sm font-medium text-red-800">{title}</h3>
              )}
              <p className="text-sm text-red-700">{children} </p>
            </div>
            {dismissBtn && (
              <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      setHidden(true);
                    }}
                    className="inline-flex bg-red-50 rounded-md p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                  >
                    <span className="sr-only">Dismiss error alert</span>
                    <XIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
